import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { DEVICE_WIDTHS, previewFrameSrc, useSiteReveal, type DeviceMode, type DsiSourceInfo } from '../../live-preview';
import { usePreviewGameLists } from '../../hooks/usePreviewGameLists';
import { useSiteBridge } from '../../useSiteBridge';
import { gameListLabel, type GamePage, type SiteSourceContent } from '../../site-games';
import { GameListSheet, type GameListSheetPublish } from '../GameListSheet';
import {
  Stage,
  DeviceViewport,
  Frame,
  Loading,
  Hint,
  HintText,
  EditHalo,
  EditPill,
  EditPillText,
} from '../LivePreview/LivePreview.styles';

export type GameListPreviewProps = {
  /** The brand's deployed site base URL. Empty shows the loader. */
  siteUrl: string;
  /** Which page of the site to open. */
  page: GamePage;
  /** The list open in the Game Lists tab (the hint says when the page does not show it). */
  dsId: string;
  /** Draft orders by list, shared with the Game Lists tab. */
  drafts: Record<string, string[]>;
  onDraftChange: (dsId: string, ids: string[] | null) => void;
  /** List contents (the site's own order) already read, and a list read here for the tab to keep. */
  contents: Record<string, SiteSourceContent>;
  onContent: (dsId: string, content: SiteSourceContent) => void;
  providerNames: Record<string, string>;
  /** What to call a list (the page's title for it); defaults to a name read from its id. */
  listLabel?: (dsId: string) => string;
  /** The list opened in the reorder sheet here (null once it closes). */
  onListOpen?: (dsId: string | null) => void;
  /** Publish / Undo publish for a list opened here; without it the sheet cannot publish. */
  publishFor?: (dsId: string) => GameListSheetPublish | undefined;
  device?: DeviceMode;
};

/**
 * The live site with the DRAFT game-list orders applied: frames the page, connects over the dsi bridge and
 * pins every drafted list shown there, so each section rendering a list shows its new order. The page's game
 * lists are editable in place: hovering one lays a "Reorder games" overlay over it, and clicking opens it in
 * the reorder sheet (`usePreviewGameLists`). Drafts are the tab's own, so a change made here shows in the tab
 * too. Nothing is saved; the pins live only in this frame and are re-applied if the owner navigates the site.
 */
export const GameListPreview = ({
  siteUrl,
  page,
  dsId,
  drafts,
  onDraftChange,
  contents,
  onContent,
  providerNames,
  listLabel = gameListLabel,
  onListOpen,
  publishFor,
  device = 'desktop',
}: GameListPreviewProps): React.ReactElement => {
  const { t } = useTranslation();
  const frameSrc = React.useMemo(() => previewFrameSrc(siteUrl, page), [siteUrl, page]);
  const { revealed, onLoad, iframeRef } = useSiteReveal(frameSrc);
  // Keep reading the page's sources until the open list and every drafted list have loaded.
  const draftIds = React.useMemo(() => Object.keys(drafts), [drafts]);
  const listsLoaded = React.useCallback(
    (sources: DsiSourceInfo[]) => [dsId, ...draftIds].every((id) => sources.some((s) => s.id === id)),
    [dsId, draftIds],
  );
  const { status, url, sources, sourcesSettled, onFrameLoad } = useSiteBridge(frameSrc, iframeRef, listsLoaded);
  const listOnPage = sources.some((s) => s.id === dsId);
  const games = usePreviewGameLists({
    iframeRef,
    frameSrc,
    drafts,
    onDraftChange,
    contents,
    onContent,
  });
  const { pageReady, pinDrafts } = games;

  const handleLoad = React.useCallback((): void => {
    onLoad();
    onFrameLoad();
  }, [onLoad, onFrameLoad]);

  // Syncs with the framed page connecting or navigating (click capture off, its lists forgotten)...
  React.useEffect(() => {
    if (status === 'ready') pageReady();
  }, [status, url, pageReady]);

  // Tells the caller which list the reorder sheet shows (Game Lists opens it there too, to publish it).
  const openedId = games.opened?.dsId ?? null;
  React.useEffect(() => {
    onListOpen?.(openedId);
  }, [openedId, onListOpen]);

  // ...and with its lists loading: pin the drafts of the lists it shows.
  React.useEffect(() => {
    if (status === 'ready') pinDrafts(sources);
  }, [status, url, sources, pinDrafts]);

  if (!frameSrc) {
    return (
      <Stage $fill>
        <Loading $hidden={false} />
      </Stage>
    );
  }

  const { overlay, tileCue } = games;
  const cue = overlay ?? tileCue;

  return (
    <Stage $fill $device={device}>
      <DeviceViewport $width={DEVICE_WIDTHS[device]}>
        <Frame
          ref={iframeRef}
          src={frameSrc}
          title={t('Live website preview')}
          onLoad={handleLoad}
          $revealed={revealed}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
        <Loading $hidden={revealed} aria-hidden={revealed || undefined} />
        {revealed && cue ? (
          /* Passive on purpose: the pointer stays with the site, so the page scrolls under this, the
             carousel's own arrows still work, and the click that opens the list comes back over the
             bridge (the site swallows it there, so nothing launches). */
          <EditHalo
            style={{ left: cue.rect.x, top: cue.rect.y, width: cue.rect.width, height: cue.rect.height }}
            aria-hidden
          >
            <EditPill>
              <Icon name="grid" size={11} />
              <EditPillText text="Reorder games" />
            </EditPill>
          </EditHalo>
        ) : null}
        {revealed ? (
          <Hint>
            <Icon name="eye" size={13} />
            {sourcesSettled && !listOnPage ? (
              <HintText text="This list isn't on this page. Your draft shows wherever the list appears." />
            ) : (
              <HintText text="Click a game list on your site to reorder it. Changes show in this preview only." />
            )}
          </Hint>
        ) : null}
      </DeviceViewport>
      {games.opened ? (
        <GameListSheet
          title={listLabel(games.opened.dsId)}
          games={games.opened.games}
          failed={games.opened.failed}
          providerNames={providerNames}
          dirty={games.opened.dirty}
          onReorder={games.reorder}
          onReset={games.reset}
          onClose={games.close}
          publish={publishFor?.(games.opened.dsId)}
        />
      ) : null}
    </Stage>
  );
};

export default GameListPreview;
